import { useState } from "react";
import { phases } from "@/data/roadmap";
import { ROADMAP } from "@/constants/testIds/roadmap";
import { SectionLabel } from "./ExecSummary";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Clock, Users, ListChecks } from "lucide-react";

export default function Phases() {
  const [active, setActive] = useState(phases[0].id);
  const activePhase = phases.find((p) => p.id === active);

  return (
    <section
      id="phases"
      data-testid={ROADMAP.phasesSection}
      className="py-20 lg:py-28 border-b border-[#DEE2E6] bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="02" label="Implementation Roadmap" />
        <div className="grid lg:grid-cols-12 gap-6 mt-3 items-end">
          <h2 className="lg:col-span-7 font-heading text-3xl md:text-4xl lg:text-5xl tracking-tighter font-bold">
            Six phases. One disciplined path to go-live.
          </h2>
          <p className="lg:col-span-5 text-[#495057] leading-relaxed">
            Click any phase to see the activities, deliverables and accountable parties. Phases are
            sequential, but training and change management run continuously from Phase 2.
          </p>
        </div>

        {/* Stepper */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border border-[#0A0A0A]">
          {phases.map((p, i) => {
            const isActive = p.id === active;
            return (
              <button
                key={p.id}
                onClick={() => setActive(p.id)}
                data-testid={ROADMAP.phaseCard(p.id)}
                className={`relative text-left p-5 transition-colors duration-200 border-r border-b border-[#0A0A0A] ${
                  i % 6 === 5 ? "lg:border-r-0" : ""
                } ${i >= 3 ? "md:border-b-0" : "md:border-b-0"} ${
                  isActive
                    ? "bg-[#0A0A0A] text-white"
                    : "bg-white hover:bg-[#F8F9FA] text-[#0A0A0A]"
                }`}
              >
                <div
                  className={`font-mono text-xs ${
                    isActive ? "text-[#FF4F00]" : "text-[#495057]"
                  }`}
                >
                  {p.number}
                </div>
                <div className="font-heading font-semibold mt-2 leading-tight">{p.name}</div>
                <div
                  className={`text-[11px] uppercase tracking-[0.15em] mt-3 ${
                    isActive ? "text-white/60" : "text-[#495057]"
                  }`}
                >
                  {p.duration}
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            data-testid={ROADMAP.phasePanel}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="border-x border-b border-[#0A0A0A] bg-[#F8F9FA] p-8 lg:p-10"
          >
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#FF4F00] font-mono">
                  Phase {activePhase.number}
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-1">
                  {activePhase.name}
                </h3>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 border border-[#0A0A0A] bg-white text-xs uppercase tracking-[0.15em]">
                <Clock size={12} />
                {activePhase.duration} · {activePhase.status}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <DetailBlock icon={<ListChecks size={16} />} title="Key Activities">
                <ul className="space-y-2.5">
                  {activePhase.activities.map((a) => (
                    <li key={a} className="grid grid-cols-[auto_1fr] gap-2 text-sm">
                      <span className="text-[#FF4F00] font-mono">▸</span>
                      <span className="text-[#0A0A0A] leading-relaxed">{a}</span>
                    </li>
                  ))}
                </ul>
              </DetailBlock>

              <DetailBlock icon={<CheckCircle2 size={16} />} title="Deliverables">
                <ul className="space-y-2.5">
                  {activePhase.deliverables.map((d) => (
                    <li key={d} className="grid grid-cols-[auto_1fr] gap-2 text-sm">
                      <CheckCircle2 size={14} className="text-[#0B7A75] mt-0.5 flex-shrink-0" />
                      <span className="text-[#0A0A0A] leading-relaxed">{d}</span>
                    </li>
                  ))}
                </ul>
              </DetailBlock>

              <DetailBlock icon={<Users size={16} />} title="Responsible Parties">
                <ul className="space-y-2">
                  {activePhase.parties.map((r) => (
                    <li
                      key={r}
                      className="text-sm px-3 py-2 border border-[#DEE2E6] bg-white"
                    >
                      {r}
                    </li>
                  ))}
                </ul>
              </DetailBlock>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function DetailBlock({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#0A0A0A] mb-4">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}
